/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { setDiff, mapBy, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import CountryCode from '@linc-technologies/linced-common/enums/country-code';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import config from 'linced-app/config/environment';
import { observes } from '@ember-decorators/object';
import {
  compact,
  mapBy as mapByArray,
  objectAt,
} from '@linc-technologies/linced-common/utils/arrays';

export default class FormController extends Controller {
  // Attrs
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @alias('schema.hasDirtyAttributes')
  recordIsDirty;

  schema = null;

  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @controller()
  application;

  // State
  @tracked
  deleteSectionDialogOpen = false;

  deleteInProgress = false;

  @tracked
  expandedSections = [];

  enrolmentIntroductionDialogOpen = false;

  @tracked
  enrolmentIntroduction = '';

  @action
  setEnrolmentIntroduction(value) {
    this.enrolmentIntroduction = value;
  }

  isSortable = false;

  @tracked
  sectionTitle = '';

  @action
  setSectionTitle(value) {
    this.sectionTitle = value;
  }

  @tracked
  sectionDescription = '';

  @action
  setSectionDescription(value) {
    this.sectionDescription = value;
  }

  @tracked
  sectionDialogOpen = false;

  saveInProgress = false;

  // Methods
  @observes('recordIsDirty')
  _updateDirtyFlag() {
    this.application.set('applicationFormUnsaved', this.recordIsDirty);
  }

  @computed(
    'schema.{sections.[],primaryContacts,emergencyContacts,medicalContacts,medicalConditions}',
  )
  get allSections() {
    let sections = this.schema.sections.slice();
    sections.push(
      ...compact([
        this.schema.primaryContacts,
        this.schema.emergencyContacts,
        this.schema.medicalContacts,
        this.schema.medicalConditions,
      ]),
    );
    return sections;
  }

  @computed('session.school.jurisdictionalId')
  get enrolmentUrl() {
    let school = this.get('session.school');
    let countryCode = CountryCode.getLabel(school.countryCode);
    let jurisdictionalId = school.jurisdictionalId;
    return `${config.APP.enrolmentHost}/apply/${countryCode}/${jurisdictionalId}?preview=${this.schema.id}`;
  }

  @mapBy('schema.sections', 'fieldIds')
  selectedLearnerFieldIds;

  @computed('model.learnerFields.[]', 'selectedLearnerFieldIds.[]')
  get selectedLearnerFields() {
    let fields = this.get('model.learnerFields');
    let selectedIds = this.get('schema.sections').reduce(function (
      ids,
      section,
    ) {
      ids.push(...mapByArray(section.get('fields'), 'fieldId'));
      return ids;
    }, []);
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('model.learnerFields', 'selectedLearnerFields')
  availableLearnerFields;

  @computed('model.caregiverFields.[]', 'schema.primaryContacts.fieldIds')
  get selectedPrimaryContactFields() {
    let fields = this.get('model.caregiverFields');
    let selectedIds = mapByArray(this.schema.primaryContacts.fields, 'fieldId');
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('model.caregiverFields', 'selectedPrimaryContactFields')
  availablePrimaryContactFields;

  @computed('model.medicalFields.[]', 'schema.medicalContacts.fieldIds')
  get selectedMedicalContactFields() {
    let fields = this.get('model.medicalFields');
    let selectedIds = mapByArray(this.schema.medicalContacts.fields, 'fieldId');
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('model.medicalFields', 'selectedMedicalContactFields')
  availableMedicalContactFields;

  @computed('model.caregiverFields.[]', 'schema.emergencyContacts.fieldIds')
  get selectedEmergencyContactFields() {
    let fields = this.get('model.caregiverFields');
    let selectedIds = mapByArray(
      this.schema.emergencyContacts.fields,
      'fieldId',
    );
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('model.caregiverFields', 'selectedEmergencyContactFields')
  availableEmergencyContactFields;

  @tracked
  section;

  @tracked
  sectionToDelete;

  @tracked
  selectedField;

  @action
  toggleSection(section) {
    if (this.expandedSections.includes(section)) {
      this.expandedSections = this.expandedSections.filter(
        (s) => s !== section,
      );
    } else {
      this.expandedSections = [...this.expandedSections, section];
    }
  }

  // isContactSection receives an extra parameter when called by the template in order to trigger
  // the fieldIds computed property on all the sections to be calculated.  Otherwise, it's deemed unused and thus
  // won't be computed at all - resulting in all fields always being available.
  @action
  isContactSection(section) {
    switch (section) {
      case this.schema.primaryContacts:
      case this.schema.emergencyContacts:
        return true;
      default:
        return false;
    }
  }

  @action
  isMedicalContactSection(section) {
    switch (section) {
      case this.schema.medicalContacts:
        return true;
      default:
        return false;
    }
  }

  @action
  isConditionSection(section) {
    switch (section) {
      case this.schema.medicalConditions:
        return true;
      default:
        return false;
    }
  }

  @action
  isPrimaryContactSection(section) {
    return this.schema.primaryContacts === section;
  }

  @action
  availableFields(section) {
    switch (section) {
      case this.schema.primaryContacts:
        return this.availablePrimaryContactFields;
      case this.schema.emergencyContacts:
        return this.availableEmergencyContactFields;
      case this.schema.medicalContacts:
        return this.availableMedicalContactFields;
      default:
        return this.availableLearnerFields;
    }
  }

  @action
  addField(section, field) {
    let props = field.getProperties(
      'isSiblingLinked',
      'key',
      'inputType',
      'dataType',
      'label',
      'default',
      'placeholder',
      'htmlAttributes',
      'isRequired',
      'optionsFromEnum',
    );
    // `options` is a fragment, so needs to be copied into the new field fragment.
    if (!isEmpty(field.options)) {
      props.options = field.options.copy();
    }
    // `default` is also a fragment, so needs to be copied into the new field fragment too.
    if (!isEmpty(field.default)) {
      props.default = field.default.copy();
    }
    props.fieldId = field.get('id');
    props.index = section.get('fields.length') + 1;
    section
      .get('fields')
      .pushObject(this.store.createFragment('enrolments/field', props));
    this.selectedField = null;
  }

  @action
  removeField(section, field) {
    section.get('fields').removeObject(field);
  }

  @action
  toggleSectionDialog() {
    this.toggleProperty('sectionDialogOpen');
    if (!this.sectionDialogOpen) {
      this.resetSectionForm();
    }
  }

  @action
  editSection(section) {
    this.section = section;
    this.sectionTitle = section.get('title');
    this.sectionDescription = section.get('description');

    this.sectionDialogOpen = true;
  }

  @action
  saveSection() {
    let section = this.section;

    if (isEmpty(section)) {
      // Creating a new section
      let section = this.get('schema.sections').createFragment({
        title: this.sectionTitle,
        description: this.sectionDescription,
        index: this.get('schema.sections.length') + 1,
      });

      // Expand the new section by default, so that fields can be added.
      this.expandedSections = [...this.expandedSections, section];
    } else {
      // Editing and existing section
      section.set('title', this.sectionTitle);
      section.set('description', this.sectionDescription);
    }

    this.sectionDialogOpen = false;
    this.resetSectionForm();
  }

  @action
  deleteSection(section) {
    this.sectionToDelete = section;
    this.deleteSectionDialogOpen = true;
  }

  @action
  confirmDeleteSection(confirmed) {
    if (confirmed) {
      this.get('schema.sections').removeObject(this.sectionToDelete);
    }

    // Tidy up / close dialog.
    this.deleteSectionDialogOpen = false;
    this.sectionToDelete = null;
  }

  @action
  saveSchema() {
    let schema = this.schema;
    schema.set('schoolCode', this.get('session.school.jurisdictionalId'));
    schema.set('schoolName', this.get('session.school.name'));
    // TODO: We do not store an id on the enrolment schema so we can't do the
    //  blobby serving. This will work until we nuke the unauthenticated
    //  media routes.
    schema.set(
      'schoolLogoUrl',
      `${config.APP.apiHost}/media/${config.APP.apiPath}/media/${this.get(
        'session.school.logoMedia',
      )}/logo.png}`,
    );
    schema.save().then(() => {
      this.paperToaster.show('Enrolment form saved successfully.', {
        duration: 4000,
        position: 'top right',
        toastClass: 'success',
      });
    });
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // sourceList/targetList may be a fields FragmentArray (sections, the
    // other caller of this action, are a plain array - hence objectAt() from
    // arrays.js rather than calling .objectAt() directly). FragmentArray
    // gets .splice() from the fragment-array-mutable initializer (an
    // EmberObject with the MutableArray mixin, not a real Array, has no
    // native one otherwise) - see that initializer for why it has to be
    // patched in rather than used natively. That splice() returns a .copy()
    // of each removed fragment (needed there so a captured "removed" item
    // can't reflect replace() reusing the same instance in place) - grab the
    // original reference before removing, so reordering within the same
    // list doesn't swap the fragment out for an unnecessary clone and lose
    // its identity.
    const item = objectAt(sourceList, sourceIndex);
    sourceList.splice(sourceIndex, 1);

    if (sourceList === targetList) {
      targetList.splice(targetIndex, 0, item);
    } else {
      // Fields are fragments, so if the list changes they must be copied
      // as fragments' owners can't be changed by simply moving the fragment over.
      targetList.splice(targetIndex, 0, item.copy());
    }

    targetList.forEach((entity, index) => {
      entity.set('index', index + 1);
    });
  }

  @action
  toggleEnrolmentIntroductionDialog() {
    this.enrolmentIntroduction = this.schema.introduction;
    this.toggleProperty('enrolmentIntroductionDialogOpen');
  }

  @action
  saveEnrolmentDescription() {
    this.toggleProperty('enrolmentIntroductionDialogOpen');
    this.set('schema.introduction', this.enrolmentIntroduction);
    this.enrolmentIntroduction = '';
  }

  resetSectionForm() {
    this.section = '';
    this.sectionTitle = '';
    this.sectionDescription = '';
  }
}
